/*
 * Fiche d'un groupe : ce qui concerne tout le groupe (contact, plateforme, notes),
 * puis ses participants rassemblés par n° d'entreprise. Chaque EAN d'un groupe peut
 * appartenir à une société différente : l'identité se gère participant par participant.
 */
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Archive, ArchiveRestore, ChevronRight, FileText, Loader2, Save, Search, Users, X } from 'lucide-react';
import type { AccountState, CrmContact, CrmContactDraft, Directory, ParticipantRow, SharingOperation } from './types';
import { STAGES, TEMPLATE_VARIABLES, formatBce } from './types';
import { archiveContact, fetchMergedContacts, openStoredFile, updateContact } from './api';
import { fetchGroupParticipants, groupByCompany, rolesSummary } from './participants';
import { Field, borderFor, formatDateFr, inputClass, errorMessage } from './ui';
import { PlatformSection } from './PlatformSection';
import { ParticipantPanel } from './ParticipantPanel';

interface Props {
  contact: CrmContact;
  operations: SharingOperation[];
  account?: AccountState;
  directory: Directory;
  onClose: () => void;
  onSaved: (c: CrmContact) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type GroupFields = Pick<CrmContactDraft, 'first_name' | 'last_name' | 'email' | 'phone' | 'notes'>;
const fieldsOf = (c: CrmContact): GroupFields => ({
  first_name: c.first_name ?? '', last_name: c.last_name ?? '', email: c.email ?? '', phone: c.phone ?? '', notes: c.notes ?? '',
});
const clean = (f: GroupFields): GroupFields => ({
  first_name: f.first_name?.trim() || null,
  last_name: f.last_name?.trim() || null,
  email: f.email?.trim().toLowerCase() || null,
  phone: f.phone?.trim() || null,
  notes: f.notes?.trim() || null,
});
/** Représentant(e) du contrat (« Représenté(e) par ») : prénom et nom. */
const withRepresentative = (f: GroupFields): CrmContactDraft => ({
  ...f, representative: [f.first_name, f.last_name].filter(Boolean).join(' ') || null,
});
const vars = (...keys: string[]) => keys.map(key => ({ key, label: TEMPLATE_VARIABLES.find(v => v.key === key)?.label ?? key }));

export function GroupDrawer({ contact, operations, account, directory, onClose, onSaved }: Props) {
  const groupName = contact.groupe?.trim() ?? '';
  const [card, setCard] = useState(contact);
  const [draft, setDraft] = useState<GroupFields>(() => fieldsOf(contact));
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<ParticipantRow[] | null>(null);
  const [merged, setMerged] = useState<CrmContact[]>([]);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchGroupParticipants(groupName), fetchMergedContacts(contact.id).catch(() => [])])
      .then(async ([participants, companies]) => {
        if (cancelled) return;
        setRows(participants);
        setMerged(companies);
        // Garde la liste des EAN de la carte alignée sur les fiches participants (voyant Compte).
        const known = new Map(contact.meters.filter(m => m.participant_id).map(m => [m.participant_id as string, m]));
        const same = participants.length === known.size && participants.every(p => known.has(p.id));
        if (participants.length > 0 && !same) {
          const updated = await updateContact(contact.id, {
            meters: participants.map(p => ({
              name: p.name, ean_code: p.ean_code ?? '', meter_address: p.address,
              meter_type: known.get(p.id)?.meter_type ?? null, participant_id: p.id,
            })),
          }).catch(() => null);
          if (updated && !cancelled) { setCard(updated); onSaved(updated); }
        }
      })
      .catch(err => { if (!cancelled) { setRows([]); toast.error(`Participants du groupe : ${errorMessage(err)}`); } });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(clean(draft)) !== JSON.stringify(clean(fieldsOf(card)));
  const emailError = draft.email?.trim() && !EMAIL_RE.test(draft.email.trim()) ? 'Adresse e-mail invalide' : undefined;
  const current: CrmContact = { ...card, ...clean(draft) } as CrmContact;
  const operationName = operations.find(o => o.id === card.sharing_operation_id)?.name;
  const stage = STAGES.find(s => s.key === 'active')!;

  const blocks = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (rows ?? []).filter(p => !q || [p.name, p.ean_code, p.company_name, p.company_number].some(v => (v ?? '').toLowerCase().includes(q)));
    return groupByCompany(list);
  }, [rows, search]);
  const stats = useMemo(() => {
    const roles: Record<string, number> = {};
    for (const p of rows ?? []) roles[p.type ?? 'consumer'] = (roles[p.type ?? 'consumer'] ?? 0) + 1;
    return { eans: rows?.length ?? 0, companies: groupByCompany(rows ?? []).filter(b => b.companyNumber).length, roles };
  }, [rows]);

  const save = async () => {
    if (emailError) { toast.error(emailError); return; }
    setSaving(true);
    try {
      const updated = await updateContact(card.id, withRepresentative(clean(draft)));
      setCard(updated);
      setDraft(fieldsOf(updated));
      onSaved(updated);
      toast.success('Groupe enregistré');
    } catch (err) {
      toast.error(`Erreur : ${errorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const patchAndNotify = async (patch: CrmContactDraft, message: string) => {
    const updated = await updateContact(card.id, patch);
    setCard(updated);
    onSaved(updated);
    toast.success(message);
  };

  const toggleArchive = async () => {
    const archived = !card.archived_at;
    if (archived && !window.confirm('Archiver ce groupe ? Il disparaît du tableau (visible avec le filtre « Archivés »). Les fiches participants ne sont pas modifiées.')) return;
    try {
      const result = await archiveContact(card.id, archived);
      onSaved(result);
      toast.success(archived ? 'Groupe archivé' : 'Groupe restauré');
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const close = () => {
    if (dirty && !window.confirm('Les modifications du groupe ne sont pas enregistrées. Fermer quand même ?')) return;
    onClose();
  };

  const sectionTitle = (t: string, extra?: ReactNode) => (
    <div className="flex items-center justify-between gap-2">
      <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{t}</h4>
      {extra}
    </div>
  );
  const openRow = rows?.find(p => p.id === openId) ?? null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={close} />
      <div className="relative w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
        {/* En-tête */}
        <div className="px-6 py-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
                <Users className="w-4 h-4 text-violet-600 shrink-0" /><span className="truncate">{groupName}</span>
              </h3>
              <p className="flex items-center gap-1.5 text-xs text-neutral-500 mt-0.5">
                <span className={`w-2 h-2 rounded-full ${stage.dot}`} />Groupe · {stage.label}
                {operationName && <span>· {operationName}</span>}
                {card.archived_at && <span className="text-red-600 font-medium">· archivé</span>}
              </p>
            </div>
            <button onClick={close} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400" aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
          {rows && (
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{stats.eans} EAN</span>
              {stats.companies > 0 && (
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                  {stats.companies} n° d’entreprise{stats.companies > 1 ? 's' : ''}
                </span>
              )}
              <span className="text-[11px] text-neutral-500">{rolesSummary(stats.roles)}</span>
            </div>
          )}
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <section className="space-y-3">
            {sectionTitle('Contact du groupe')}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prénom du représentant(e)" vars={vars('{{prenom}}')}>
                <input value={draft.first_name ?? ''} onChange={e => setDraft(d => ({ ...d, first_name: e.target.value }))}
                  className={`${inputClass} border-neutral-200`} />
              </Field>
              <Field label="Nom du représentant(e)" vars={vars('{{nom_famille}}')}>
                <input value={draft.last_name ?? ''} onChange={e => setDraft(d => ({ ...d, last_name: e.target.value }))}
                  className={`${inputClass} border-neutral-200`} />
              </Field>
              <Field label="E-mail" vars={vars('{{email}}')} error={emailError}>
                <input type="email" value={draft.email ?? ''} onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
                  placeholder="contact@groupe.be" className={`${inputClass} ${borderFor(false, emailError)}`} />
              </Field>
              <Field label="Téléphone" vars={vars('{{telephone}}')}>
                <input type="tel" value={draft.phone ?? ''} onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))}
                  placeholder="+32 …" className={`${inputClass} border-neutral-200`} />
              </Field>
            </div>
            <p className="text-[11px] text-neutral-500">
              Contact unique du groupe : il reçoit l’invitation et le message de bienvenue. Chaque participant garde son e-mail de facturation.
            </p>
          </section>

          <section className="space-y-3">
            {sectionTitle('Plateforme')}
            <PlatformSection contact={current} account={account} operations={operations} dirty={dirty} onPatched={patchAndNotify} />
          </section>

          <section className="space-y-3">
            {sectionTitle(`Participants${rows ? ` (${rows.length})` : ''}`, rows && rows.length > 8 && (
              <div className="relative">
                <Search className="w-3 h-3 text-neutral-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nom, EAN, société…"
                  className="w-44 pl-6 pr-2 py-1 text-[11px] border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50" />
              </div>
            ))}
            {rows === null ? (
              <div className="flex items-center gap-2 text-xs text-neutral-400 py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Chargement des participants…</div>
            ) : blocks.length === 0 ? (
              <p className="text-xs text-neutral-400 py-4 text-center">{search ? 'Aucun participant ne correspond' : 'Aucun participant dans ce groupe'}</p>
            ) : blocks.map(block => {
              const contract = block.companyNumber ? merged.find(m => formatBce(m.company_number) === block.companyNumber) : undefined;
              return (
                <div key={block.companyNumber ?? 'none'} className="rounded-xl border border-neutral-200 overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-3 py-2 bg-neutral-50 border-b border-neutral-100">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-900 truncate">
                        {block.companyNumber ? block.companyName ?? 'Dénomination non renseignée' : 'Sans n° d’entreprise'}
                      </p>
                      <p className="text-[11px] text-neutral-500">
                        {block.companyNumber ? `${block.companyNumber} · ` : ''}{block.participants.length} EAN
                      </p>
                    </div>
                    {contract?.contract_signed_at && (
                      contract.signed_contract_path ? (
                        <button
                          onClick={() => openStoredFile('crm-documents', String(contract.signed_contract_path)).catch(err => toast.error(errorMessage(err)))}
                          className="flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-800 whitespace-nowrap"
                        >
                          <FileText className="w-3.5 h-3.5" />Contrat signé le {formatDateFr(contract.contract_signed_at)}
                        </button>
                      ) : (
                        <span className="text-[11px] text-neutral-500 whitespace-nowrap">Contrat signé le {formatDateFr(contract.contract_signed_at)}</span>
                      )
                    )}
                  </div>
                  <ul className="divide-y divide-neutral-100">
                    {block.participants.map(p => (
                      <li key={p.id}>
                        <button onClick={() => setOpenId(p.id)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-amber-50/40">
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm text-neutral-900 truncate">{p.name || 'Sans nom'}</span>
                            <span className="block font-mono text-[10px] text-neutral-400">{p.ean_code}</span>
                          </span>
                          <span className="text-[11px] text-neutral-500 whitespace-nowrap">
                            {p.entry_date ? `Entrée le ${formatDateFr(p.entry_date)}` : 'Date d’entrée manquante'}
                          </span>
                          <ChevronRight className="w-3.5 h-3.5 text-neutral-300 shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </section>

          <section className="space-y-2">
            {sectionTitle('Notes')}
            <textarea rows={3} value={draft.notes ?? ''} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
              className={`${inputClass} border-neutral-200 resize-none`} placeholder="Notes internes" />
          </section>
        </div>

        {/* Pied */}
        <div className="px-6 py-4 border-t border-neutral-100 bg-neutral-50/60 flex items-center justify-between gap-3 shrink-0">
          <button onClick={toggleArchive} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-neutral-500 hover:text-red-600 hover:bg-red-50">
            {card.archived_at ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
            {card.archived_at ? 'Restaurer' : 'Archiver'}
          </button>
          <button onClick={save} disabled={saving || !dirty} className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-40">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer
          </button>
        </div>

        {openRow && (
          <ParticipantPanel
            key={openRow.id}
            participant={openRow}
            groupName={groupName}
            operations={operations}
            directory={directory}
            onBack={() => setOpenId(null)}
            onSaved={updated => setRows(prev => (prev ?? []).map(p => (p.id === updated.id ? updated : p)))}
          />
        )}
      </div>
    </div>
  );
}
